class CompareManager {
  static STORAGE_KEY = 'shopify_compare_products';
  static MAX_PRODUCTS = 14;

  static getProducts() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  static saveProducts(products, silent = false) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(products));
    if (!silent) {
      document.dispatchEvent(new CustomEvent('compare:updated', { detail: { products } }));
    }
  }

  static isInCompare(productId) {
    return this.getProducts().some((p) => String(p.id) === String(productId));
  }

  static addProduct(product) {
    const products = this.getProducts();
    const exists = products.findIndex((p) => String(p.id) === String(product.id));

    if (exists > -1) {
      products[exists] = product;
    } else {
      if (products.length >= this.MAX_PRODUCTS) {
        products.shift();
      }
      products.push(product);
    }

    this.saveProducts(products);
    return products;
  }

  static removeProduct(productId) {
    const products = this.getProducts().filter((p) => String(p.id) !== String(productId));
    this.saveProducts(products);
    return products;
  }

  static async refreshProduct(handle) {
    try {
      const response = await fetch(`${window.shopUrl}/products/${handle}.js`);
      if (!response.ok) return null;
      const data = await response.json();
      return this.formatProductFromJson(data);
    } catch {
      return null;
    }
  }

  static formatProductFromJson(data) {
    const variant = data.variants.find((v) => v.available) || data.variants[0];
    const options = data.options || [];

    let color = '';
    let size = '';

    options.forEach((option, index) => {
      const name = option.name.toLowerCase();
      const values = [...new Set(data.variants.map((v) => v[`option${index + 1}`]).filter(Boolean))].join(', ');

      if (name.includes('color') || name.includes('colour')) {
        color = values;
      } else if (name.includes('size')) {
        size = values;
      }
    });

    const formatMoney = (cents) => {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents);
      }
      return (cents / 100).toFixed(2);
    };

    return {
      id: data.id,
      handle: data.handle,
      title: data.title,
      url: `/products/${data.handle}`,
      image: data.featured_image || (data.images && data.images[0]) || '',
      price: formatMoney(variant.price),
      priceAmount: variant.price,
      compareAtPrice: variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '',
      compareAtPriceAmount: variant.compare_at_price > variant.price ? variant.compare_at_price : 0,
      description: this.stripHtml(data.description),
      available: data.available,
      color,
      size,
      hasVariants: data.variants.length > 1 || (data.variants.length === 1 && data.variants[0].title !== 'Default Title'),
      variantId: variant.id,
    };
  }

  static mergeProduct(stored, fresh) {
    if (!fresh) return stored;
    if (!stored) return fresh;

    const merged = { ...stored, ...fresh };

    if (stored.priceAmount === fresh.priceAmount) {
      merged.price = stored.price;
    }
    if (stored.compareAtPriceAmount === fresh.compareAtPriceAmount) {
      merged.compareAtPrice = stored.compareAtPrice;
    }

    return merged;
  }

  static stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  }
}

if (!customElements.get('compare-button')) {
  customElements.define(
    'compare-button',
    class CompareButton extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('[data-compare-trigger]');
        this.productData = null;

        const script = this.querySelector('[data-compare-product]');
        if (script) {
          try {
            this.productData = JSON.parse(script.textContent);
          } catch {
            this.productData = null;
          }
        }

        this.button?.addEventListener('click', this.onClick.bind(this));
        document.addEventListener('compare:updated', this.onCompareUpdated.bind(this));
        this.updateState();
      }

      onClick() {
        if (!this.productData) return;

        CompareManager.addProduct(this.productData);
        this.updateState();

        const drawer = document.querySelector('compare-drawer');
        if (drawer) drawer.open();
      }

      onCompareUpdated() {
        this.updateState();
      }

      updateState() {
        if (!this.productData || !this.button) return;
        const isActive = CompareManager.isInCompare(this.productData.id);
        this.button.classList.toggle('is-active', isActive);
        this.button.setAttribute('aria-pressed', isActive);
      }
    }
  );
}

if (!customElements.get('compare-drawer')) {
  customElements.define(
    'compare-drawer',
    
    class CompareDrawer extends HTMLElement {
      constructor() {
        super();
        this.overlay = this.querySelector('[data-compare-overlay]');
        this.productsContainer = this.querySelector('[data-compare-products]');
        this.closeButton = this.querySelector('[data-compare-close]');

        this.closeButton?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', (e) => {
          if (e.target === this.overlay) this.close();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.overlay?.classList.contains('is-open')) {
            this.close();
          }
        });

        document.addEventListener('compare:updated', () => this.render());

        this.render();
      }

      open() {
        this.render();
        this.overlay?.classList.add('is-open');
        document.body.classList.add('compare-opened');
      }

      close() {
        this.overlay?.classList.remove('is-open');
        document.body.classList.remove('compare-opened');
      }

      async render() {
        if (!this.productsContainer) return;

        let products = CompareManager.getProducts();

        if (products.length === 0) {
          this.productsContainer.innerHTML = `<div class="compare-empty">${window.compareStrings?.empty || 'No products to compare'}</div>`;
          return;
        }

        const refreshed = await Promise.all(
          products.map(async (product) => {
            if (product.handle) {
              const fresh = await CompareManager.refreshProduct(product.handle);
              return CompareManager.mergeProduct(product, fresh);
            }
            return product;
          })
        );

        const current = JSON.stringify(CompareManager.getProducts());
        const updated = JSON.stringify(refreshed);
        if (current !== updated) {
          CompareManager.saveProducts(refreshed, true);
        }
        products = refreshed;

        this.productsContainer.innerHTML = products.map((product) => this.renderProductColumn(product)).join('');

        this.productsContainer.querySelectorAll('[data-compare-remove]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            CompareManager.removeProduct(btn.dataset.compareRemove);
            this.render();
          });
        });

        this.productsContainer.querySelectorAll('[data-compare-add]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToCart(btn.dataset.compareAdd);
          });
        });
      }

      renderProductColumn(product) {
        const priceHtml = product.compareAtPrice
          ? `<span class="compare-product-col__price-compare">${this.escapeHtml(product.compareAtPrice)}</span><span class="compare-product-col__price-current">${this.escapeHtml(product.price)}</span>`
          : `<span class="compare-product-col__price-current">${this.escapeHtml(product.price)}</span>`;

        const availabilityClass = product.available ? 'compare-badge--in-stock' : 'compare-badge--out-of-stock';
        const availabilityText = product.available
          ? window.compareStrings?.inStock || 'In stock'
          : window.compareStrings?.outOfStock || 'Out of stock';

        const actionHtml = product.hasVariants
          ? `<a href="${product.url}" class="compare-product-col__action">${window.compareStrings?.selectOptions || 'Select options'}</a>`
          : `<button type="button" class="compare-product-col__action" data-compare-add="${product.variantId}" ${!product.available ? 'disabled' : ''}>${window.compareStrings?.addToCart || 'Add to cart'}</button>`;

        return `
          <div class="compare-product-col">
            <div class="compare-product-col__header">
              <button type="button" class="compare-product-col__remove" data-compare-remove="${product.id}" aria-label="${window.compareStrings?.remove || 'Remove'}">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path stroke="currentColor" stroke-linecap="round" d="M3 3l8 8M11 3l-8 8"/></svg>
              </button>
              <a href="${product.url}" class="compare-product-col__image-link">
                <img src="${product.image}" alt="${this.escapeHtml(product.title)}" class="compare-product-col__image" loading="lazy" width="200" height="267">
              </a>
              <a href="${product.url}" class="compare-product-col__title">${this.escapeHtml(product.title)}</a>
              <div class="compare-product-col__price">${priceHtml}</div>
              ${actionHtml}
            </div>
            <div class="compare-product-col__row compare-product-col__row--description">${this.escapeHtml(product.description)}</div>
            <div class="compare-product-col__row">
              <span class="compare-badge ${availabilityClass}">${availabilityText}</span>
            </div>
            <div class="compare-product-col__row">${this.escapeHtml(product.color) || '—'}</div>
            <div class="compare-product-col__row">${this.escapeHtml(product.size) || '—'}</div>
          </div>
        `;
      }

      async addToCart(variantId) {
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } };

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;
        delete config.headers['Content-Type'];

        try {
          const response = await fetch(window.routes.cart_add_url, config);
          const data = await response.json();

          if (data.status) return;

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'compare-drawer', cartData: data });
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch {
          /* silent */
        }
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
      }
    }
  );
}

window.CompareManager = CompareManager;
